/**
 * The agenda row model and everything derived from it.
 *
 * There is one agenda per module, owned by the facilitators and edited in the
 * facilitator view. It is stored server-side under the key
 * `fac-agenda-rows-<moduleId>` so every facilitator sees the same plan.
 *
 * Participants see the same rows, not a second hand-maintained copy: the
 * participant module page reads them from `GET /agenda` (which strips the
 * facilitator-only fields) and turns them into each day's morning/afternoon
 * lists via `withLiveAgenda`. That keeps the two views in sync for every
 * module automatically — reorder a block in the facilitator agenda and the
 * participant agenda follows.
 *
 * The hand-written `session.agenda` arrays in `data/modules.ts` remain the
 * fallback, used when a module has no facilitator rows yet or the request
 * fails.
 */

import {
  type AgendaItem,
  type FacilitatorAgendaSeedRow,
  type Module,
  type Session,
} from "@/data/modules";

// ---------------------------------------------------------------------------
// Row model
// ---------------------------------------------------------------------------

export interface AgendaRow {
  id: string;
  type: "day" | "activity" | "lunch" | "note";
  day: number;
  number: string;
  name: string;
  duration: string;
  owner: string;
  comment?: string;
  /** Day rows only: the time this day starts, e.g. "09:00" */
  startTime?: string;
}

/**
 * The subset of a row a participant is allowed to see. `owner` and `comment`
 * are facilitation internals and are dropped server-side; this type documents
 * the shape that actually arrives from `GET /agenda`.
 */
export type ParticipantAgendaRow = Pick<AgendaRow, "type" | "day" | "name" | "duration"> &
  Partial<Pick<AgendaRow, "startTime">>;

/**
 * The agenda rows for a module before any server state is applied.
 *
 * Mirrors what the facilitator agenda tab shows on a module nobody has edited
 * yet: the explicit seed from `data/modules.ts` when there is one, otherwise
 * rows synthesised from the participant agenda so the table isn't empty. Both
 * views resolve rows the same way — server state if it exists, this seed if
 * not — which is what keeps them showing the same plan before the first edit
 * is ever saved.
 */
export function seedAgendaRows(module: Module): AgendaRow[] {
  if (module.facilitator.agenda?.length) {
    return buildAgendaRows(module.facilitator.agenda);
  }
  const rows: AgendaRow[] = [];
  const sessions = [...module.during.sessions].sort((a, b) => a.day - b.day);

  for (const session of sessions) {
    const n = session.day;
    rows.push({
      id: crypto.randomUUID(),
      type: "day",
      day: n,
      number: "",
      name: `DAY ${n}`,
      duration: "",
      owner: "",
      startTime: "09:00",
    });
    if (!session.agenda) {
      // Sessions with sections only get the day header (no agenda to iterate).
      continue;
    }
    session.agenda.morning.forEach((item, idx) => {
      rows.push({
        id: crypto.randomUUID(),
        type: "activity",
        day: n,
        number: String(idx + 1),
        name: item.title,
        duration: item.duration,
        owner: "All",
      });
    });
    rows.push({
      id: crypto.randomUUID(),
      type: "lunch",
      day: n,
      number: "",
      name: "Lunch",
      duration: "1h",
      owner: "",
    });
    session.agenda.afternoon.forEach((item, idx) => {
      rows.push({
        id: crypto.randomUUID(),
        type: "activity",
        day: n,
        number: String(idx + 1),
        name: item.title,
        duration: item.duration,
        owner: "All",
      });
    });
  }
  return rows;
}

/** Facilitator-state key holding the agenda rows for one module. */
export function agendaStorageKey(moduleId: number): string {
  return `fac-agenda-rows-${moduleId}`;
}

/** Build full agenda rows (ids + day grouping) from a compact module-data seed. */
export function buildAgendaRows(seed: FacilitatorAgendaSeedRow[]): AgendaRow[] {
  let day = 0;
  return seed.map((r) => {
    if (r.type === "day") {
      day += 1;
    }
    const row: AgendaRow = {
      id: crypto.randomUUID(),
      type: r.type,
      day: day === 0 ? 1 : day,
      number: r.number ?? "",
      name: r.name,
      duration: r.duration ?? "",
      owner: r.owner ?? "",
    };
    if (r.comment !== undefined) {
      row.comment = r.comment;
    }
    if (r.startTime !== undefined) {
      row.startTime = r.startTime;
    }
    return row;
  });
}

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------

/** "45 min" | "1h" | "1h 30 min" | "90" → minutes. Unparseable → 0. */
export function parseDurationToMinutes(s: string): number {
  if (!s) {
    return 0;
  }
  const norm = s.trim().toLowerCase();
  const hm = norm.match(/^(\d+(?:\.\d+)?)\s*h(?:ours?)?\s*(?:(\d+)\s*m(?:in)?s?)?$/);
  if (hm) {
    return Math.round(parseFloat(hm[1]!) * 60) + parseInt(hm[2] ?? "0", 10);
  }
  const m = norm.match(/^(\d+)\s*m(?:in)?s?$/);
  if (m) {
    return parseInt(m[1]!, 10);
  }
  const plain = norm.match(/^(\d+)$/);
  if (plain) {
    return parseInt(plain[1]!, 10);
  }
  return 0;
}

/** "09:30" → minutes since midnight. Unparseable → 09:00. */
export function parseTimeStr(s: string): number {
  const match = (s ?? "").match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    return parseInt(match[1]!, 10) * 60 + parseInt(match[2]!, 10);
  }
  return 9 * 60;
}

/** Minutes since midnight → "09:30" (facilitator agenda column format). */
export function minutesToTime(mins: number): string {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Minutes since midnight → "9am" / "5:30pm" (participant day-header format). */
function formatClock(mins: number): string {
  const h24 = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const suffix = h24 < 12 ? "am" : "pm";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, "0")}${suffix}`;
}

/** Two clock times → "9am – 5:30pm". */
export function formatTimeRange(startMins: number, endMins: number): string {
  return `${formatClock(startMins)} – ${formatClock(endMins)}`;
}

// ---------------------------------------------------------------------------
// Facilitator rows → participant agenda
// ---------------------------------------------------------------------------

export interface DerivedDayAgenda {
  morning: AgendaItem[];
  afternoon: AgendaItem[];
  /** Derived from the day's start time plus the sum of its durations. */
  timeRange: string | null;
}

/**
 * Row types participants never see. `note` rows are facilitator annotations
 * rather than agenda items; breaks and intros are ordinary `activity` rows and
 * are shown, so the participant agenda reflects the real shape of the day.
 */
const HIDDEN_ROW_TYPES = new Set<AgendaRow["type"]>(["note"]);

interface DayAccumulator {
  morning: AgendaItem[];
  afternoon: AgendaItem[];
  start: number | null;
  cursor: number;
  afterLunch: boolean;
}

/**
 * Turn a module's agenda rows into per-day morning/afternoon lists.
 *
 * The `lunch` row is the divider: rows before it land in the morning, rows
 * after it in the afternoon, and lunch itself is not listed (the participant
 * view draws its own lunch bar). Rows are grouped by their own `day` field,
 * which the facilitator drag-and-drop keeps aligned to the nearest preceding
 * day header.
 */
export function deriveDayAgendas(
  rows: readonly ParticipantAgendaRow[],
): Map<number, DerivedDayAgenda> {
  const acc = new Map<number, DayAccumulator>();

  const forDay = (day: number): DayAccumulator => {
    let entry = acc.get(day);
    if (!entry) {
      entry = { morning: [], afternoon: [], start: null, cursor: 0, afterLunch: false };
      acc.set(day, entry);
    }
    return entry;
  };

  for (const row of rows) {
    if (typeof row?.day !== "number") {
      continue;
    }
    const entry = forDay(row.day);

    if (row.type === "day") {
      const start = parseTimeStr(row.startTime ?? "09:00");
      entry.start = start;
      entry.cursor = start;
      entry.afterLunch = false;
      continue;
    }

    entry.cursor += parseDurationToMinutes(row.duration ?? "");

    if (row.type === "lunch") {
      entry.afterLunch = true;
      continue;
    }
    if (HIDDEN_ROW_TYPES.has(row.type)) {
      continue;
    }
    const title = (row.name ?? "").trim();
    if (!title) {
      continue;
    }
    const item: AgendaItem = { title, duration: row.duration ?? "" };
    (entry.afterLunch ? entry.afternoon : entry.morning).push(item);
  }

  const derived = new Map<number, DerivedDayAgenda>();
  for (const [day, entry] of acc) {
    derived.set(day, {
      morning: entry.morning,
      afternoon: entry.afternoon,
      timeRange:
        entry.start != null && entry.cursor > entry.start
          ? formatTimeRange(entry.start, entry.cursor)
          : null,
    });
  }
  return derived;
}

/**
 * Overlay the facilitator agenda onto a module's sessions.
 *
 * `serverRows` are what the facilitators have actually saved. When they have
 * saved nothing — the common case, since the agenda tab only writes on the
 * first edit — this falls back to `seedAgendaRows`, exactly as that tab does.
 * Without that fallback the participant view would keep rendering its own
 * hand-written agenda and the two would appear to disagree even though no one
 * had changed anything.
 *
 * Any individual day whose rows produce no agenda items is left untouched, so
 * a partial agenda can never blank out the hand-written one in
 * `data/modules.ts`.
 */
export function withLiveAgenda(
  module: Module,
  serverRows: readonly ParticipantAgendaRow[] | undefined,
): Module {
  const rows: readonly ParticipantAgendaRow[] = serverRows?.length
    ? serverRows
    : seedAgendaRows(module);
  if (!rows.length) {
    return module;
  }
  const derived = deriveDayAgendas(rows);
  let changed = false;

  const sessions: Session[] = module.during.sessions.map((session) => {
    const day = derived.get(session.day);
    if (!day || (day.morning.length === 0 && day.afternoon.length === 0)) {
      return session;
    }
    changed = true;
    return {
      ...session,
      agenda: { morning: day.morning, afternoon: day.afternoon },
      // The hand-written time range wins. A derived one sums every row in the
      // day, so Module 3 Day 1 would read "9am - 8pm" once travel and the team
      // dinner are counted, rather than the 5:30pm its working day ends at.
      // Only fill one in for a session that has no range of its own.
      ...(session.timeRange || !day.timeRange ? {} : { timeRange: day.timeRange }),
    };
  });

  if (!changed) {
    return module;
  }
  return { ...module, during: { ...module.during, sessions } };
}
